/**
 * MyMemory MT Provider
 * 100% FREE - No API key required!
 * Limit: 5,000 words per day per IP
 * API Docs: https://mymemory.translated.net/doc/spec.php
 */

import { MTProvider } from "./mtEngine";

const LANG_NAME_TO_CODE: Record<string, string> = {
    english: "en",
    afrikaans: "af",
    portuguese: "pt",
    french: "fr",
    spanish: "es",
    german: "de",
    dutch: "nl",
    italian: "it",
    arabic: "ar",
    chinese: "zh",
    japanese: "ja",
    korean: "ko",
    russian: "ru",
    polish: "pl",
    swedish: "sv",
    norwegian: "no",
    danish: "da",
    finnish: "fi",
    czech: "cs",
    hungarian: "hu",
    romanian: "ro",
    greek: "el",
    turkish: "tr",
    hebrew: "he",
    hindi: "hi",
    thai: "th",
    indonesian: "id",
    malay: "ms",
    swahili: "sw",
    zulu: "zu",
    xhosa: "xh",
    sotho: "st",
    tswana: "tn",
    venda: "ve",
    tsonga: "ts",
    ndebele: "nr",
};

const normaliseCode = (lang: string): string => {
    if (!lang) return lang;
    if (lang.length <= 5 && !lang.includes(" ")) return lang.toLowerCase();
    return LANG_NAME_TO_CODE[lang.toLowerCase()] ?? lang.toLowerCase();
};

// Luso-African Portuguese variants (pt-AO, pt-MZ, pt-CV, pt-GW, pt-ST, pt-TL) are not offered
// by MT engines, so MyMemory gets pt-PT for them. The project keeps its own language code.
const toMyMemoryTarget = (lang: string): string => {
    const code = normaliseCode(lang);
    return code.startsWith("pt-") && code !== "pt-br" ? "pt-PT" : code;
};

class MyMemoryProvider implements MTProvider {
    private readonly API_ENDPOINT = "https://api.mymemory.translated.net/get";
    private readonly DAILY_WORD_LIMIT = 5000;
    private usageKey = "mymemory_daily_usage";

    getName(): string {
        return "MyMemory (Free)";
    }

    isAvailable(): boolean {
        // Always available - no API key needed!
        return true;
    }

    getUsage(): number {
        return this.getDailyUsage();
    }

    getMonthlyLimit(): number {
        // Daily limit, but we track it
        return this.DAILY_WORD_LIMIT * 30;
    }

    resetUsage(): void {
        this.resetDailyUsage();
    }

    /**
     * Translate a single text
     */
    async translate(
        text: string,
        sourceLang: string,
        targetLang: string,
    ): Promise<string> {
        if (!text || text.trim().length === 0) {
            return "";
        }

        // Check daily limit (approximate word count)
        const wordCount = text.split(/\s+/).length;
        if (!this.canMakeRequest(wordCount)) {
            const remaining = this.getRemainingQuota();
            throw new Error(
                `Daily limit reached. ${remaining} words remaining today.`,
            );
        }

        try {
            const url = new URL(this.API_ENDPOINT);
            url.searchParams.set("q", text);
            url.searchParams.set(
                "langpair",
                `${normaliseCode(sourceLang)}|${toMyMemoryTarget(targetLang)}`,
            );

            const response = await fetch(url.toString());

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`,
                );
            }

            const data = await response.json();

            if (data.responseStatus !== 200) {
                throw new Error(data.responseDetails || "Translation failed");
            }

            const translation = data.responseData.translatedText;

            // Track usage
            this.trackUsage(wordCount);

            return translation;
        } catch (error) {
            console.error("MyMemory error:", error);
            throw new Error(
                error instanceof Error ? error.message : "Translation failed",
            );
        }
    }

    /**
     * Translate multiple texts (one by one - no batch API)
     */
    async translateBatch(
        texts: string[],
        sourceLang: string,
        targetLang: string,
    ): Promise<string[]> {
        if (texts.length === 0) {
            return [];
        }

        const translations: string[] = [];

        for (const text of texts) {
            if (!text || text.trim().length === 0) {
                translations.push("");
                continue;
            }

            try {
                const translation = await this.translate(
                    text,
                    sourceLang,
                    targetLang,
                );
                translations.push(translation);

                // Add small delay to avoid rate limiting
                await new Promise((resolve) => setTimeout(resolve, 500));
            } catch (error) {
                console.error(
                    `MyMemory batch error for text "${text.substring(0, 50)}...":`,
                    error,
                );
                translations.push("");
            }
        }

        return translations;
    }

    /**
     * Track daily usage
     */
    private trackUsage(wordCount: number): void {
        const data = this.getDailyUsageData();
        const updatedData = {
            words: data.words + wordCount,
            date: new Date().toDateString(),
        };
        localStorage.setItem(this.usageKey, JSON.stringify(updatedData));

        // Log warning if approaching limit
        const percentage = (updatedData.words / this.DAILY_WORD_LIMIT) * 100;
        if (percentage >= 80) {
            console.warn(
                `⚠️ MyMemory: ${percentage.toFixed(0)}% of daily limit used`,
            );
        }
    }

    /**
     * Check if request can be made
     */
    private canMakeRequest(wordCount: number): boolean {
        const data = this.getDailyUsageData();
        return data.words + wordCount <= this.DAILY_WORD_LIMIT;
    }

    /**
     * Get daily usage data
     */
    private getDailyUsageData(): { words: number; date: string } {
        try {
            const stored = localStorage.getItem(this.usageKey);
            if (!stored) {
                return { words: 0, date: new Date().toDateString() };
            }

            const data = JSON.parse(stored);
            const today = new Date().toDateString();

            // Reset if new day
            if (data.date !== today) {
                return { words: 0, date: today };
            }

            return data;
        } catch (error) {
            return { words: 0, date: new Date().toDateString() };
        }
    }

    /**
     * Get current daily usage
     */
    private getDailyUsage(): number {
        return this.getDailyUsageData().words;
    }

    /**
     * Reset daily usage
     */
    private resetDailyUsage(): void {
        localStorage.setItem(
            this.usageKey,
            JSON.stringify({
                words: 0,
                date: new Date().toDateString(),
            }),
        );
    }

    /**
     * Get remaining quota
     */
    private getRemainingQuota(): number {
        const used = this.getDailyUsage();
        return Math.max(0, this.DAILY_WORD_LIMIT - used);
    }
}

export default MyMemoryProvider;
